import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSync, FaBox, FaUserTie, FaChartLine, FaCog, FaMobileAlt, FaTimesCircle, FaArrowRight } from 'react-icons/fa';
import theme from '../../theme';

const FeaturesSection = styled.section`
  padding: 5rem 0;
  background-color: ${theme.colors.white};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 2rem;
  color: ${theme.colors.secondary};
  
  span {
    color: ${theme.colors.primary};
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.darkGray};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: ${theme.colors.lightGray};
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.boxShadow};
  }
`;

const FeatureIcon = styled.div`
  margin-bottom: 1.5rem;
  color: ${theme.colors.primary};
  font-size: 2.5rem;
  text-align: center;
  display: flex;
  justify-content: center;
  
  svg {
    width: 2.5rem;
    height: 2.5rem;
  }
`;

const FeatureTitle = styled.h3`
  margin-bottom: 1rem;
  color: ${theme.colors.secondary};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.darkGray};
  margin-bottom: 1.5rem;
`;

const FeatureLink = styled(Link)`
  color: ${theme.colors.primary};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  
  &:after {
    content: '→';
    margin-left: 0.5rem;
    transition: ${theme.transition};
  }
  
  &:hover:after {
    margin-left: 0.75rem;
  }
`;

const CtaBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 1.25rem 2.5rem;
  border-radius: 14px;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    padding: 1.25rem 1.5rem;
    gap: 0.75rem;
    text-align: center;
  }
`;

const BannerOldWay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #ef4444;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  span {
    font-size: 1.05rem;
    font-weight: 600;
    color: rgba(255,255,255,0.7);
    text-decoration: line-through;
    text-decoration-color: #ef4444;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    span { font-size: 0.95rem; }
  }
`;

const BannerNewWay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${theme.colors.primary};
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  span {
    font-size: 1.15rem;
    font-weight: 800;
    color: white;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    span { font-size: 1.05rem; }
  }
`;

const Features = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const features = [
    {
      icon: <FaSync />,
      titleKey: 'features.items.supplyChain.title',
      descriptionKey: 'features.items.supplyChain.description',
      link: `/${lang}/features/supply-chain`
    },
    {
      icon: <FaBox />,
      titleKey: 'features.items.stockManagement.title',
      descriptionKey: 'features.items.stockManagement.description',
      link: `/${lang}/features/stock-management`
    },
    {
      icon: <FaUserTie />,
      titleKey: 'features.items.salesRep.title',
      descriptionKey: 'features.items.salesRep.description',
      link: `/${lang}/features/sales-rep`
    },
    {
      icon: <FaChartLine />,
      titleKey: 'features.items.analytics.title',
      descriptionKey: 'features.items.analytics.description',
      link: `/${lang}/features/analytics`
    },
    {
      icon: <FaCog />,
      titleKey: 'features.items.erpIntegration.title',
      descriptionKey: 'features.items.erpIntegration.description',
      link: `/${lang}/features/erp-integration`
    },
    {
      icon: <FaMobileAlt />,
      titleKey: 'features.items.mobileOrder.title',
      descriptionKey: 'features.items.mobileOrder.description',
      link: `/${lang}/features/mobile-order`
    }
  ];
  
  return (
    <FeaturesSection>
      <div className="container">
        <SectionTitle dangerouslySetInnerHTML={{ __html: t('features.title') }} />
        <SectionSubtitle>
          {t('features.subtitle')}
        </SectionSubtitle>

        <CtaBanner>
          <BannerOldWay>
            <FaTimesCircle />
            <span>WhatsApp, Telefon &amp; Excel ile Siparişe Son!</span>
          </BannerOldWay>
          <BannerNewWay>
            <FaArrowRight />
            <span>{t('features.ctaBanner')}</span>
          </BannerNewWay>
        </CtaBanner>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{t(feature.titleKey)}</FeatureTitle>
              <FeatureDescription>{t(feature.descriptionKey)}</FeatureDescription>
              <FeatureLink to={feature.link}>{t('common.learnMore')}</FeatureLink>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </div>
    </FeaturesSection>
  );
};

export default Features; 